/**
 * チケットの「入店可能時間」まわりの表記を、チケット単位で出し分ける。
 *
 * 2026-08 の表記変更で『来店可能時間』を『入店可能時間』に改めたが、運用側の要望は
 * 「リリース以降に公開申請されたチケットだけを新表記にする」
 * 「非公開→公開の切り替えでは表記を変えない」の2点だった。
 *
 * API は公開申請の時刻を appliedAt（UNIX秒）で返す。この列は表記変更のリリースで
 * 新設したもので、過去のチケットにはバックフィルしていない。したがって
 *   - appliedAt あり = リリース以降に申請された  -> 新表記
 *   - appliedAt が null = リリース以前に申請された -> 旧表記のまま
 * となり、日付のしきい値を持たなくても判定できる（リリースがずれても仕様が崩れない）。
 *
 * 公開・非公開の切り替えでは appliedAt を更新しないので、
 * 何度 非公開→公開 を繰り返しても表記は変わらない。
 *
 * なお一覧テーブルの列ヘッダと CSV の見出しは1つの見出しに新旧のチケットが混ざるため、
 * ここでは出し分けず新表記に統一している。
 */
export type TicketVisitLabelSource = {
  appliedAt: number | null;
};

/** リリース以降に公開申請されたチケットか（＝新表記を使うか） */
export const usesEntryTimeLabel = (ticket: TicketVisitLabelSource): boolean => ticket.appliedAt != null;

/**
 * 入店可能な時刻の範囲に対する見出し。
 * 旧チケットは既存の文言をそのまま保つため、画面ごとの旧文言を legacyLabel で渡す。
 */
export const visitTimeLabel = (ticket: TicketVisitLabelSource, legacyLabel = "来店可能時間"): string =>
  usesEntryTimeLabel(ticket) ? "入店可能時間" : legacyLabel;

/** 入店可能な時刻の開始側の見出し（ダッシュボードのチケット詳細で使う） */
export const visitStartTimeLabel = (ticket: TicketVisitLabelSource): string =>
  usesEntryTimeLabel(ticket) ? "入店開始時間" : "来店開始時間";

/** 入店可能な時刻の終了側の見出し（ダッシュボードのチケット詳細で使う） */
export const visitEndTimeLabel = (ticket: TicketVisitLabelSource): string =>
  usesEntryTimeLabel(ticket) ? "入店終了時間" : "来店終了時間";
